import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { FaSearch } from "react-icons/fa";

import { AppDispatch } from "../../store";
import ECSLoader from "../../components/common/EcsLoader";
import { useDebounce } from "../../util/hooks/useDebounce";
import { Broadcast } from "../../types/broadcastType";
import Pagination from "../../components/common/Pagination";
import { fetchBroadcasts } from "../../features/message/Messageservice";
import { Link } from "react-router-dom";
import { formatDate } from "../../util/functions/UniversalFunctions";

export default function BroadcastList() {
  const dispatch = useDispatch<AppDispatch>();

  const [broadcasts, setBroadcasts] = useState<Broadcast[]>([]);
  const [pageInfo, setPageInfo] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  const [filters, setFilters] = useState({
    name: "",
    templateName: "",
    audienceType: "",
    status: "",
  });

  const debouncedFilters = useDebounce(filters, 500);

  useEffect(() => {
    setPage(0);
  }, [debouncedFilters]);

  const loadBroadcasts = async () => {
    setLoading(true);
    setError("");

    try {
      const result = await dispatch(
        fetchBroadcasts({
          page,
          size: pageSize,
        }),
      ).unwrap();

      setBroadcasts(result.results || []);
      setPageInfo(result.page || {});
    } catch (err: any) {
      setError(err?.message || "Failed to load broadcasts");
      setBroadcasts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBroadcasts();
  }, [page, pageSize, debouncedFilters]);

  const filteredBroadcasts = broadcasts.filter((item) => {
    const matchesName =
      !filters.name ||
      item.name?.toLowerCase().includes(filters.name.toLowerCase());

    const matchesTemplate =
      !filters.templateName ||
      item.templateName
        ?.toLowerCase()
        .includes(filters.templateName.toLowerCase());

    const matchesAudience =
      !filters.audienceType || item.audienceType === filters.audienceType;

    const matchesStatus = !filters.status || item.status === filters.status;

    return matchesName && matchesTemplate && matchesAudience && matchesStatus;
  });

  return (
    <div className="bg-white rounded-3xl p-4 sm:p-5 mt-5 w-full relative">
      <ECSLoader show={loading} />

      {error && (
        <div className="text-red-500 mb-3 text-sm font-medium">{error}</div>
      )}

      <div className="flex justify-end mb-4">
        <Pagination
          page={page}
          setPage={setPage}
          totalPages={pageInfo.count ?? 0}
          totalItems={pageInfo.total ?? 0}
          pageSize={pageSize}
          setPageSize={setPageSize}
          loading={loading}
        />
      </div>

      <div className="flex flex-wrap gap-2 mb-4 sm:hidden">
        <SearchBox
          value={filters.name}
          onChange={(v) =>
            setFilters((prev) => ({
              ...prev,
              name: v,
            }))
          }
          placeholder="Search name"
        />

        <SearchBox
          value={filters.templateName}
          onChange={(v) =>
            setFilters((prev) => ({
              ...prev,
              templateName: v,
            }))
          }
          placeholder="Search template"
        />

        <select
          value={filters.audienceType}
          onChange={(e) =>
            setFilters((prev) => ({
              ...prev,
              audienceType: e.target.value,
            }))
          }
          className="border rounded-md h-8 px-2 text-xs font-medium outline-none focus:ring-1 focus:ring-blue-400"
        >
          <option value="">All Audiences</option>
          <option value="FILE">FILE</option>
          <option value="TAGS">TAGS</option>
          <option value="NUMBERS">NUMBERS</option>
        </select>

        <select
          value={filters.status}
          onChange={(e) =>
            setFilters((prev) => ({
              ...prev,
              status: e.target.value,
            }))
          }
          className="border rounded-md h-8 px-2 text-xs font-medium outline-none focus:ring-1 focus:ring-blue-400"
        >
          <option value="">All Status</option>
          <option value="COMPLETED">COMPLETED</option>
          <option value="FAILED">FAILED</option>
          <option value="INITIATED">INITIATED</option>
        </select>
      </div>

      <div
        className={`overflow-x-auto w-full transition-opacity duration-300 ${
          loading ? "opacity-40 pointer-events-none" : "opacity-100"
        }`}
      >
        <table className="w-full min-w-[1000px] xl:table-fixed uppercase text-sm border-separate border-spacing-y-2">
          <thead className="text-gray-600">
            <tr>
              {/* <th className="p-2 text-left font-bold w-[5%]">ID</th> */}

              <th className="p-2 text-left font-bold w-[18%]">Name</th>
              <th className="p-2 text-left font-bold w-[10%]">Audience</th>
              {/* <th className="p-2 text-left font-bold w-[18%]">Template</th> */}

              <th className="p-2 text-left font-bold w-[12%]">Status</th>
              <th className="p-2 text-left font-bold w-[15%]">Scheduled</th>

              <th className="p-2 text-left font-bold w-[8%]">Sent</th>
              <th className="p-2 text-left font-bold w-[8%]">Delivered</th>
              <th className="p-2 text-left font-bold w-[8%]">Read</th>

              <th className="p-2 text-left font-bold w-[8%]">Failed</th>
              <th className="p-2 text-left font-bold w-[8%]">Action</th>
            </tr>

            <tr>
              <th className="py-1">
                <SearchBox
                  value={filters.name}
                  onChange={(v) =>
                    setFilters((prev) => ({
                      ...prev,
                      name: v,
                    }))
                  }
                  placeholder="Search name"
                />
              </th>

              <th className="py-1 pr-2">
                <select
                  value={filters.audienceType}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      audienceType: e.target.value,
                    }))
                  }
                  className="w-full border rounded-md h-8 px-2 text-xs font-medium outline-none focus:ring-1 focus:ring-blue-400"
                >
                  <option value="">All</option>
                  <option value="FILE">FILE</option>
                  <option value="TAGS">TAGS</option>
                  <option value="NUMBERS">NUMBERS</option>
                </select>
              </th>

              {/* <th className="py-1">
                <SearchBox
                  value={filters.templateName}
                  onChange={(v) =>
                    setFilters((prev) => ({
                      ...prev,
                      templateName: v,
                    }))
                  }
                  placeholder="Search template"
                />
              </th> */}

              <th className="py-1">
                <select
                  value={filters.status}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      status: e.target.value,
                    }))
                  }
                  className="w-full border rounded-md h-8 px-2 text-xs font-medium outline-none focus:ring-1 focus:ring-blue-400"
                >
                  <option value="">All</option>
                  <option value="COMPLETED">COMPLETED</option>
                  <option value="FAILED">FAILED</option>
                  <option value="INITIATED">INITIATED</option>
                </select>
              </th>

              <th />
              <th />
              <th />
            </tr>
          </thead>

          <tbody>
            {filteredBroadcasts.length === 0 && !loading ? (
              <tr>
                <td colSpan={8} className="text-center py-8 text-gray-400">
                  No broadcasts found.
                </td>
              </tr>
            ) : (
              filteredBroadcasts.map((item, index) => (
                <tr
                  key={item.id ?? index}
                  className="font-medium even:bg-gray-50 odd:bg-white hover:bg-gray-100 transition-colors"
                >
                  {/* <td className="p-2 text-gray-500">{item.id}</td> */}

                  <td className="p-2 truncate" title={item.name}>
                    {item.name || "-"}
                  </td>

                  <td className="p-2">
                    <AudienceBadge type={item.audienceType} />
                  </td>

                  {/* <td className="p-2 truncate" title={item.templateName}>
                    {item.templateName || "-"}
                  </td> */}

                  <td className="p-2">
                    <StatusBadge status={item.status} />
                  </td>

                  <td className="p-2 text-gray-500 text-xs">
                    {item.scheduledAtLocal || formatDate(item.scheduledAt)}
                  </td>

                  <td className="p-2">
                    <div className="font-semibold">{item.sent}</div>
                  </td>

                  <td className="p-2">
                    <div className="font-semibold">{item.delivered}</div>
                  </td>

                  <td className="p-2">
                    <div className="font-semibold">{item.read}</div>
                  </td>

                  <td className="p-2">
                    <div
                      className={`text-xs ${
                        item.failed > 0 ? "text-red-500" : "text-text-secondary"
                      }`}
                    >
                      {item.failed} failed
                    </div>
                  </td>

                  <td className="p-2">
                    <Link
                      to={`${item.id}`}
                      className="text-web-secondary transition-all ease-out hover:translate-y-2 hover:underline"
                    >
                      View
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function StatusBadge({ status }: { status: string }) {
  const isCompleted = status === "COMPLETED";
  const isFailed = status === "FAILED";
  const isInitiated = status === "ENQUIRED";

  let className = "bg-gray-50 text-gray-600";

  if (isCompleted) {
    className = "bg-green-50 text-web-primary";
  } else if (isFailed) {
    className = "bg-red-50 text-red-500";
  } else if (isInitiated) {
    className = "bg-yellow-50 text-yellow-600";
  }

  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full ${className}`}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
          isCompleted
            ? "bg-green-500"
            : isFailed
              ? "bg-red-500"
              : isInitiated
                ? "bg-yellow-500"
                : "bg-gray-400"
        }`}
      />

      {status || "UNKNOWN"}
    </span>
  );
}

function AudienceBadge({ type }: { type: string }) {
  return (
    <span className="inline-flex items-center text-xs font-semibold px-2.5 py-1 rounded-full bg-blue-50 text-blue-600">
      {type || "-"}
    </span>
  );
}

function SearchBox({
  value = "",
  onChange,
  placeholder = "Search",
}: {
  value?: string;
  onChange?: (val: string) => void;
  placeholder?: string;
}) {
  return (
    <div className="relative w-[70%] max-w-[200px]">
      <input
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        type="text"
        placeholder={placeholder}
        className="w-full border rounded-md h-8 pl-8 pr-2 text-xs font-medium outline-none focus:ring-1 focus:ring-blue-400"
      />

      <FaSearch className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none" />
    </div>
  );
}